/**
 * OffersSubscriptionsTab — Admin dashboard for Offers & Subscriptions.
 * iter191. Read-only aggregation view over TWO existing collections:
 *   - `campaigns`       via GET /api/admin/campaigns
 *   - `private_offers`  via GET /api/admin/private-offers
 * Actions on individual beneficiaries reuse the existing endpoints:
 *   PATCH  /api/admin/private-offers/{id}   (extend / change plan / suspend / resume)
 *   DELETE /api/admin/private-offers/{id}   (cancel)
 * Every mutation appends an immutable entry to the offer's history[]
 * (server-side). This tab renders — but never edits — that history.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Gift, RefreshCw, Loader2, Users2, Eye, X, Clock, CalendarDays,
  Pause, Play, Ban, Repeat, Plus, ShieldCheck, Search,
} from "lucide-react";
import { useI18n } from "../../lib/i18n";
import { apiClient, formatApiError } from "../../lib/api";

const PLANS = [
  { id: "individual_plus", ar: "الأفراد بلس", en: "Individual Plus" },
  { id: "dealer_pro",       ar: "التاجر برو",  en: "Dealer Pro" },
  { id: "enterprise",       ar: "المؤسسات",    en: "Enterprise" },
];

function planLabel(id, lang) {
  const p = PLANS.find((x) => x.id === id);
  return p ? (lang === "ar" ? p.ar : p.en) : (id || "—");
}

function fmtDate(iso, lang) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(lang === "ar" ? "ar-SA" : "en-GB"); }
  catch { return String(iso).slice(0, 19); }
}

function fmtDay(iso, lang) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(lang === "ar" ? "ar-SA" : "en-GB"); }
  catch { return String(iso).slice(0, 10); }
}

function daysBetween(fromIso, toIso) {
  if (!fromIso || !toIso) return null;
  const a = new Date(fromIso).getTime();
  const b = new Date(toIso).getTime();
  if (Number.isNaN(a) || Number.isNaN(b)) return null;
  return Math.ceil((b - a) / (24 * 60 * 60 * 1000));
}

/* ────────────────────────────────────────────────────────────────
 * Domain mappers — normalize campaigns & private_offers into a
 * single "offer row" the top-level table can render.
 * ─────────────────────────────────────────────────────────────── */
function campaignTypeLabel(c, lang) {
  // Public unless slug hints otherwise. Coupon = campaigns that carry a
  // discount code convention. We keep it declarative and label-only.
  const tags = (c.tags || []).map((t) => String(t).toLowerCase());
  if (tags.includes("coupon")) return lang === "ar" ? "قسيمة" : "Coupon";
  if (tags.includes("manual")) return lang === "ar" ? "يدوي" : "Manual";
  return lang === "ar" ? "عام" : "Public";
}

function campaignStatus(c, lang) {
  const now = new Date();
  const start = c.start_at ? new Date(c.start_at) : null;
  const end   = c.end_at   ? new Date(c.end_at)   : null;
  if (c.archived_at) return { key: "archived", label: lang === "ar" ? "مؤرشف" : "Archived", color: "#6b6b6b" };
  if (end && end < now) return { key: "expired", label: lang === "ar" ? "منتهي" : "Expired", color: "#8a8a8a" };
  if (!c.is_active) return { key: "paused",   label: lang === "ar" ? "متوقف" : "Paused", color: "#a16207" };
  if (start && start > now) return { key: "scheduled", label: lang === "ar" ? "مجدول" : "Scheduled", color: "#1d4ed8" };
  return { key: "active", label: lang === "ar" ? "نشط" : "Active", color: "#0a4f3a" };
}

function offerStatus(o, lang) {
  const now = new Date();
  const start = o.start_at ? new Date(o.start_at) : null;
  const end   = o.end_at   ? new Date(o.end_at)   : null;
  if (!o.active) return { key: "suspended", label: lang === "ar" ? "معلّق" : "Suspended", color: "#b91c1c" };
  if (o.reverted_at) return { key: "expired", label: lang === "ar" ? "منتهي" : "Expired", color: "#8a8a8a" };
  if (end && end < now) return { key: "expired", label: lang === "ar" ? "منتهي" : "Expired", color: "#8a8a8a" };
  if (o.activated_at) return { key: "active", label: lang === "ar" ? "نشط" : "Active", color: "#0a4f3a" };
  if (start && start > now) return { key: "scheduled", label: lang === "ar" ? "مجدول" : "Scheduled", color: "#1d4ed8" };
  return { key: "pending", label: lang === "ar" ? "بانتظار التفعيل" : "Pending", color: "#a16207" };
}

const HISTORY_KIND = {
  granted:      { ar: "تم المنح",         en: "Granted",        color: "#8a6f2e" },
  activated:    { ar: "تم التفعيل",       en: "Activated",      color: "#0a4f3a" },
  extended:     { ar: "تم التمديد",       en: "Extended",       color: "#1d4ed8" },
  renewed:      { ar: "تم التجديد",       en: "Renewed",        color: "#1d4ed8" },
  suspended:    { ar: "تم التعليق",       en: "Suspended",      color: "#b91c1c" },
  resumed:      { ar: "تم الاستئناف",     en: "Resumed",        color: "#0a4f3a" },
  cancelled:    { ar: "تم الإلغاء",       en: "Cancelled",      color: "#b91c1c" },
  expired:      { ar: "انتهت الصلاحية",   en: "Expired",        color: "#8a8a8a" },
  reactivated:  { ar: "أُعيد التفعيل",    en: "Reactivated",    color: "#0a4f3a" },
  plan_changed: { ar: "تغيّرت الباقة",    en: "Plan Changed",   color: "#1d4ed8" },
  date_changed: { ar: "تعديل التواريخ",   en: "Date Changed",   color: "#a16207" },
};

/* ═══════════════════════════════════════════════════════════════
 *  Main component
 * ═══════════════════════════════════════════════════════════════ */
export default function OffersSubscriptionsTab() {
  const { lang } = useI18n();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [privateOffers, setPrivateOffers] = useState([]);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null); // { kind: "campaign"|"private_offer", data }

  const reload = useCallback(async () => {
    setBusy(true); setErr(null);
    try {
      const [cRes, pRes] = await Promise.all([
        apiClient.get("/admin/campaigns", { params: { limit: 500 } }),
        apiClient.get("/admin/private-offers", { params: { limit: 500 } }),
      ]);
      setCampaigns(cRes.data?.items || []);
      setPrivateOffers(pRes.data?.items || []);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  /* Unified rows -------------------------------------------------- */
  const rows = useMemo(() => {
    const campRows = campaigns.map((c) => ({
      _kind: "campaign",
      _raw: c,
      id: c.id,
      name: c.name || c.slug || "—",
      type: campaignTypeLabel(c, lang),
      plan: c.granted_plan || null,
      status: campaignStatus(c, lang),
      start_at: c.start_at,
      end_at: c.end_at,
      beneficiaries: Number(c.usage_count || 0),
    }));
    const offerRows = privateOffers.map((o) => ({
      _kind: "private_offer",
      _raw: o,
      id: o.id,
      name: o.user_name || o.user_email || (lang === "ar" ? "عرض خاص" : "Private offer"),
      type: lang === "ar" ? "خاص" : "Private",
      plan: o.plan,
      status: offerStatus(o, lang),
      start_at: o.start_at,
      end_at: o.end_at,
      beneficiaries: 1,
    }));
    const all = [...campRows, ...offerRows];
    const q = query.trim().toLowerCase();
    return q
      ? all.filter((r) =>
          (r.name || "").toLowerCase().includes(q)
          || (r.plan || "").toLowerCase().includes(q)
          || (r.type || "").toLowerCase().includes(q))
      : all;
  }, [campaigns, privateOffers, lang, query]);

  return (
    <div className="space-y-5" data-testid="offers-subs-tab">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-2xl text-[#0a0a0a] inline-flex items-center gap-2">
            <Gift className="w-6 h-6 text-[#8a6f2e]" />
            {lang === "ar" ? "العروض والاشتراكات" : "Offers & Subscriptions"}
          </h2>
          <p className="text-sm text-[#6b6b6b] mt-1">
            {lang === "ar"
              ? "لوحة موحّدة للحملات العامة والعروض الخاصة وسجل تعديل الاشتراكات."
              : "Unified view of campaigns, private offers, and subscription audit log."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute top-1/2 -translate-y-1/2 start-3 text-[#8a8a8a] pointer-events-none" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={lang === "ar" ? "بحث بالاسم/الباقة…" : "Search name/plan…"}
              className="input-dark ps-9 h-9 text-sm w-64"
              data-testid="offers-search"
            />
          </div>
          <button onClick={reload} disabled={busy} className="btn-ghost text-sm" data-testid="offers-refresh">
            <RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} /> {lang === "ar" ? "تحديث" : "Refresh"}
          </button>
        </div>
      </div>

      {err && (
        <div className="rounded-xl p-3 text-sm text-red-700 border border-red-200 bg-red-50">{String(err)}</div>
      )}

      {/* Unified offers table */}
      <div className="surface rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#0a0a0a]/[0.03] text-[#3a3a3a]">
              <tr className="text-[11px] uppercase tracking-widest">
                <th className="text-start px-4 py-3">{lang === "ar" ? "الاسم" : "Name"}</th>
                <th className="text-start px-4 py-3">{lang === "ar" ? "النوع" : "Type"}</th>
                <th className="text-start px-4 py-3">{lang === "ar" ? "الباقة الممنوحة" : "Granted plan"}</th>
                <th className="text-start px-4 py-3">{lang === "ar" ? "الحالة" : "Status"}</th>
                <th className="text-start px-4 py-3">{lang === "ar" ? "البداية" : "Start"}</th>
                <th className="text-start px-4 py-3">{lang === "ar" ? "الانتهاء" : "End"}</th>
                <th className="text-start px-4 py-3">{lang === "ar" ? "المستفيدون" : "Beneficiaries"}</th>
                <th className="text-end px-4 py-3">{lang === "ar" ? "إجراء" : "Action"}</th>
              </tr>
            </thead>
            <tbody>
              {busy && rows.length === 0 ? (
                <tr><td colSpan={8} className="text-center py-10 text-[#6b6b6b]"><Loader2 className="w-5 h-5 animate-spin inline" /></td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={8} className="text-center py-10 text-[#6b6b6b]" data-testid="offers-empty">
                  {lang === "ar" ? "لا توجد عروض بعد." : "No offers yet."}
                </td></tr>
              ) : rows.map((r) => (
                <tr key={`${r._kind}-${r.id}`} className="border-t border-[#0a0a0a]/[0.05] hover:bg-[#fafaf7]" data-testid={`offer-row-${r._kind}-${r.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-semibold text-[#0a0a0a]">{r.name}</div>
                    <code className="text-[10px] text-[#8a8a8a]">{r.id?.slice(0, 8)}</code>
                  </td>
                  <td className="px-4 py-3">{r.type}</td>
                  <td className="px-4 py-3">{r.plan ? planLabel(r.plan, lang) : <span className="text-[#8a8a8a]">—</span>}</td>
                  <td className="px-4 py-3">
                    <span className="inline-block text-[11px] font-bold px-2 py-0.5 rounded-full"
                          style={{ backgroundColor: `${r.status.color}22`, color: r.status.color }}>
                      {r.status.label}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-[#525252]">{fmtDay(r.start_at, lang)}</td>
                  <td className="px-4 py-3 text-[#525252]">{fmtDay(r.end_at, lang)}</td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1 text-[#0a0a0a]">
                      <Users2 className="w-3.5 h-3.5" /> {r.beneficiaries}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-end">
                    <button onClick={() => setSelected({ kind: r._kind, data: r._raw })}
                            className="btn-ghost text-xs"
                            data-testid={`offer-open-${r._kind}-${r.id}`}>
                      <Eye className="w-3.5 h-3.5" /> {lang === "ar" ? "تفاصيل" : "Details"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {selected && (
        <DetailModal
          entry={selected}
          lang={lang}
          onClose={() => setSelected(null)}
          onChanged={async () => { await reload(); }}
        />
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
 *  Detail modal — beneficiary drill-in
 * ═══════════════════════════════════════════════════════════════ */
function DetailModal({ entry, lang, onClose, onChanged }) {
  React.useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div onClick={onClose} className="fixed inset-0 z-50 flex items-center justify-center p-4"
         style={{ backgroundColor: "rgba(10,10,10,0.7)" }}
         data-testid="offer-detail-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-auto shadow-2xl">
        <div className="sticky top-0 bg-white flex items-center justify-between px-6 py-3 border-b border-[#0a0a0a]/[0.08] z-10">
          <div className="text-[13px] font-semibold text-[#3a3a3a]">
            {lang === "ar" ? "تفاصيل العرض والمستفيدين" : "Offer & beneficiaries"}
          </div>
          <button type="button" onClick={onClose}
                  aria-label={lang === "ar" ? "إغلاق" : "Close"}
                  title={lang === "ar" ? "إغلاق" : "Close"}
                  className="p-2 -me-2 rounded-full hover:bg-[#0a0a0a]/[0.05] text-[#525252] hover:text-[#0a0a0a]"
                  data-testid="offer-detail-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6">
          {entry.kind === "campaign"
            ? <CampaignDetail c={entry.data} lang={lang} />
            : <PrivateOfferDetail offer={entry.data} lang={lang} onChanged={onChanged} onClose={onClose} />}
        </div>
      </div>
    </div>
  );
}

/* ── Campaign read-only view (public campaigns don't track per-user
 *    beneficiaries in the current data model — surfaced honestly). */
function CampaignDetail({ c, lang }) {
  const st = campaignStatus(c, lang);
  return (
    <>
      <div className="flex items-center gap-2 mb-1">
        <h3 className="font-display text-xl">{c.name || c.slug}</h3>
        <span className="inline-block text-[11px] font-bold px-2 py-0.5 rounded-full"
              style={{ backgroundColor: `${st.color}22`, color: st.color }}>{st.label}</span>
      </div>
      {c.description && <p className="text-sm text-[#525252] mb-4">{c.description}</p>}

      <dl className="grid sm:grid-cols-2 gap-3 text-[13px] mb-5">
        <Row label={lang === "ar" ? "المعرّف" : "Slug"} v={<code className="bg-[#fafaf7] px-1 rounded">{c.slug}</code>} />
        <Row label={lang === "ar" ? "الباقة الممنوحة" : "Granted plan"} v={c.granted_plan ? planLabel(c.granted_plan, lang) : "—"} />
        <Row label={lang === "ar" ? "البداية" : "Start"} v={fmtDate(c.start_at, lang)} />
        <Row label={lang === "ar" ? "الانتهاء" : "End"} v={fmtDate(c.end_at, lang)} />
        <Row label={lang === "ar" ? "عدد المستفيدين" : "Beneficiaries"} v={Number(c.usage_count || 0)} />
        <Row label={lang === "ar" ? "الحد الأقصى" : "Max beneficiaries"} v={c.max_beneficiaries ?? (lang === "ar" ? "بلا حد" : "Unlimited")} />
      </dl>

      <div className="rounded-lg border border-[#e6e0cf] bg-[#fbf7ec] p-3 text-[12px] text-[#8a6f2e]">
        {lang === "ar"
          ? "الحملات العامة تُتتبع بإحصاء إجمالي للمستفيدين فقط — تعديلات الاشتراك تُدار عبر بطاقة كل مستفيد من صفحة \"العروض الخاصة\"."
          : "Public campaigns track only an aggregate beneficiary count. Individual subscription actions are performed from the private-offer card of each grant."}
      </div>
    </>
  );
}

/* ── Private-offer view: 1 beneficiary + immutable history + actions */
function PrivateOfferDetail({ offer: initial, lang, onChanged, onClose }) {
  const [offer, setOffer] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [showExtend, setShowExtend] = useState(false);
  const [showPlan, setShowPlan] = useState(false);
  const [extendDays, setExtendDays] = useState(30);
  const [nextPlan, setNextPlan] = useState(initial.plan);

  const refetch = useCallback(async () => {
    try {
      const { data } = await apiClient.get(`/admin/private-offers/${offer.id}`);
      if (data) setOffer(data);
    } catch { /* noop — parent will refresh */ }
  }, [offer.id]);

  const patch = async (payload, label) => {
    setBusy(true);
    try {
      await apiClient.patch(`/admin/private-offers/${offer.id}`, payload);
      toast.success(lang === "ar" ? "تم." : `Done: ${label}`);
      await refetch();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const doExtend = async () => {
    const days = Math.max(1, Math.min(3650, parseInt(extendDays || 0, 10)));
    if (!days) return;
    const base = offer.end_at ? new Date(offer.end_at) : new Date();
    const next = new Date(base.getTime() + days * 24 * 60 * 60 * 1000);
    await patch({ end_at: next.toISOString() }, "extended");
    setShowExtend(false);
  };

  const doChangePlan = async () => {
    if (!nextPlan || nextPlan === offer.plan) { setShowPlan(false); return; }
    await patch({ plan: nextPlan }, "plan_changed");
    setShowPlan(false);
  };

  const doSuspend = () => patch({ active: false }, "suspended");
  const doResume  = () => patch({ active: true },  "resumed");

  const doCancel = async () => {
    if (!window.confirm(lang === "ar" ? "إلغاء العرض نهائياً؟ لا يمكن التراجع." : "Cancel offer permanently?")) return;
    setBusy(true);
    try {
      await apiClient.delete(`/admin/private-offers/${offer.id}`);
      toast.success(lang === "ar" ? "تم الإلغاء." : "Cancelled.");
      onChanged?.();
      onClose?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const st = offerStatus(offer, lang);
  const nowIso = new Date().toISOString();
  const remaining = daysBetween(nowIso, offer.end_at);

  return (
    <>
      {/* Beneficiary summary */}
      <section className="mb-5">
        <h3 className="font-display text-xl mb-1 inline-flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-[#8a6f2e]" />
          {offer.user_name || offer.user_email || (lang === "ar" ? "مستفيد" : "Beneficiary")}
        </h3>
        <div className="text-[12px] text-[#525252] mb-3">{offer.user_email}</div>

        <dl className="grid sm:grid-cols-2 gap-3 text-[13px]">
          <Row label={lang === "ar" ? "نوع الحساب" : "Account type"} v={<AccountTypeCell userId={offer.user_id} lang={lang} />} />
          <Row label={lang === "ar" ? "المدينة" : "City"}           v={<CityCell userId={offer.user_id} lang={lang} />} />
          <Row label={lang === "ar" ? "الباقة الممنوحة" : "Granted plan"} v={planLabel(offer.plan, lang)} />
          <Row label={lang === "ar" ? "تاريخ التفعيل" : "Activation date"} v={fmtDate(offer.activated_at, lang)} />
          <Row label={lang === "ar" ? "تاريخ الانتهاء" : "Expiry date"} v={fmtDate(offer.end_at, lang)} />
          <Row
            label={lang === "ar" ? "الأيام المتبقية" : "Remaining days"}
            v={remaining === null ? "—" : (remaining <= 0
                ? (lang === "ar" ? "منتهي" : "Expired")
                : `${remaining} ${lang === "ar" ? "يوم" : "days"}`)}
          />
          <Row
            label={lang === "ar" ? "الحالة" : "Status"}
            v={<span className="inline-block text-[11px] font-bold px-2 py-0.5 rounded-full"
                     style={{ backgroundColor: `${st.color}22`, color: st.color }}>{st.label}</span>}
          />
        </dl>
      </section>

      {/* Admin actions */}
      <section className="mb-5 border-t border-[#0a0a0a]/[0.06] pt-4">
        <h4 className="text-[11px] uppercase tracking-widest text-[#8a6f2e] font-bold mb-3">
          {lang === "ar" ? "إجراءات المسؤول" : "Admin actions"}
        </h4>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setShowExtend(true)} disabled={busy}
                  className="btn-ghost text-xs" data-testid="offer-action-extend">
            <CalendarDays className="w-3.5 h-3.5" /> {lang === "ar" ? "تمديد الاشتراك" : "Extend"}
          </button>
          {offer.active ? (
            <button onClick={doSuspend} disabled={busy}
                    className="inline-flex items-center gap-1.5 rounded-full h-8 px-3 text-xs font-bold border border-amber-300 text-amber-800 hover:bg-amber-50"
                    data-testid="offer-action-suspend">
              <Pause className="w-3.5 h-3.5" /> {lang === "ar" ? "تعليق" : "Suspend"}
            </button>
          ) : (
            <button onClick={doResume} disabled={busy}
                    className="inline-flex items-center gap-1.5 rounded-full h-8 px-3 text-xs font-bold border border-emerald-300 text-emerald-800 hover:bg-emerald-50"
                    data-testid="offer-action-resume">
              <Play className="w-3.5 h-3.5" /> {lang === "ar" ? "استئناف" : "Resume"}
            </button>
          )}
          <button onClick={() => setShowPlan(true)} disabled={busy}
                  className="btn-ghost text-xs" data-testid="offer-action-change-plan">
            <Repeat className="w-3.5 h-3.5" /> {lang === "ar" ? "تغيير الباقة" : "Change plan"}
          </button>
          <button onClick={doCancel} disabled={busy}
                  className="inline-flex items-center gap-1.5 rounded-full h-8 px-3 text-xs font-bold border border-red-300 text-red-800 hover:bg-red-50"
                  data-testid="offer-action-cancel">
            <Ban className="w-3.5 h-3.5" /> {lang === "ar" ? "إلغاء" : "Cancel"}
          </button>
          {busy && <Loader2 className="w-4 h-4 animate-spin text-[#8a6f2e]" />}
        </div>

        {showExtend && (
          <div className="mt-3 flex items-end gap-2 rounded-lg border border-[#0a0a0a]/[0.08] p-3 bg-[#fafaf7]">
            <label className="text-xs">
              <span className="block text-[10px] text-[#525252] mb-1">
                {lang === "ar" ? "عدد الأيام (تُضاف لتاريخ الانتهاء الحالي)" : "Extend by (days added to current end date)"}
              </span>
              <input type="number" min={1} max={3650} value={extendDays}
                     onChange={(e) => setExtendDays(e.target.value)}
                     className="input-dark h-9 w-32 text-sm" data-testid="offer-extend-days" />
            </label>
            <button onClick={doExtend} disabled={busy}
                    className="inline-flex items-center gap-1.5 rounded-full h-9 px-4 text-xs font-bold text-white bg-[#0a4f3a]"
                    data-testid="offer-extend-confirm">
              <Plus className="w-3.5 h-3.5" /> {lang === "ar" ? "تأكيد التمديد" : "Confirm"}
            </button>
            <button onClick={() => setShowExtend(false)} className="btn-ghost text-xs">{lang === "ar" ? "إلغاء" : "Cancel"}</button>
          </div>
        )}

        {showPlan && (
          <div className="mt-3 flex items-end gap-2 rounded-lg border border-[#0a0a0a]/[0.08] p-3 bg-[#fafaf7]">
            <label className="text-xs">
              <span className="block text-[10px] text-[#525252] mb-1">
                {lang === "ar" ? "الباقة الجديدة" : "New plan"}
              </span>
              <select value={nextPlan} onChange={(e) => setNextPlan(e.target.value)}
                      className="input-dark h-9 text-sm" data-testid="offer-change-plan-select">
                {PLANS.map((p) => <option key={p.id} value={p.id}>{lang === "ar" ? p.ar : p.en}</option>)}
              </select>
            </label>
            <button onClick={doChangePlan} disabled={busy || nextPlan === offer.plan}
                    className="inline-flex items-center gap-1.5 rounded-full h-9 px-4 text-xs font-bold text-white bg-[#0a4f3a] disabled:opacity-50"
                    data-testid="offer-change-plan-confirm">
              <Repeat className="w-3.5 h-3.5" /> {lang === "ar" ? "تأكيد" : "Confirm"}
            </button>
            <button onClick={() => setShowPlan(false)} className="btn-ghost text-xs">{lang === "ar" ? "إلغاء" : "Cancel"}</button>
          </div>
        )}
      </section>

      {/* Immutable history timeline */}
      <section>
        <h4 className="text-[11px] uppercase tracking-widest text-[#8a6f2e] font-bold mb-3">
          {lang === "ar" ? "سجل الاشتراك (سجل تدقيق ثابت)" : "Subscription history (immutable audit)"}
        </h4>
        {(offer.history || []).length === 0 ? (
          <div className="text-[12px] text-[#8a8a8a]" data-testid="offer-history-empty">
            {lang === "ar" ? "لا توجد أحداث بعد." : "No events yet."}
          </div>
        ) : (
          <ul className="space-y-2" data-testid="offer-history-list">
            {[...offer.history].reverse().map((h) => {
              const meta = HISTORY_KIND[h.kind] || { ar: h.kind, en: h.kind, color: "#525252" };
              return (
                <li key={h.id || h.at} className="rounded-lg border border-[#0a0a0a]/[0.08] p-3 text-[12px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="inline-flex items-center gap-1 font-bold" style={{ color: meta.color }}>
                      <Clock className="w-3 h-3" /> {lang === "ar" ? meta.ar : meta.en}
                    </span>
                    <span className="text-[#525252]" dir="ltr">{fmtDate(h.at, lang)}</span>
                    <span className="ms-auto text-[#8a8a8a]">
                      {h.actor_admin_name || h.actor_admin_email || (lang === "ar" ? "النظام" : "system")}
                    </span>
                  </div>
                  {h.details && Object.keys(h.details).length > 0 && (
                    <div className="mt-1 text-[11px] text-[#525252]" dir="ltr">
                      <code className="bg-[#fafaf7] px-1 rounded">{JSON.stringify(h.details)}</code>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </>
  );
}

function Row({ label, v }) {
  return (
    <div>
      <dt className="text-[10px] uppercase text-[#8a8a8a]">{label}</dt>
      <dd className="text-[#0a0a0a]">{v}</dd>
    </div>
  );
}

/* ── Small async cells that pull enrichment from /users/:id -------- */
function AccountTypeCell({ userId, lang }) {
  const [val, setVal] = useState("…");
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await apiClient.get(`/users/${userId}`);
        if (!alive) return;
        setVal(data?.role || (lang === "ar" ? "—" : "—"));
      } catch { if (alive) setVal("—"); }
    })();
    return () => { alive = false; };
  }, [userId, lang]);
  return <span>{val}</span>;
}

function CityCell({ userId, lang }) {
  const [val, setVal] = useState("…");
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await apiClient.get(`/users/${userId}`);
        if (!alive) return;
        setVal(data?.city || (lang === "ar" ? "—" : "—"));
      } catch { if (alive) setVal("—"); }
    })();
    return () => { alive = false; };
  }, [userId, lang]);
  return <span>{val}</span>;
}

// Re-export utility for the campaign detail
function campaignStatus_export(c, lang) { return campaignStatus(c, lang); }
